import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Phone, ArrowRight, AlertCircle } from "lucide-react";
import { api, formatUSD } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { BRAND } from "@/data/content";

const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setStatus("paid");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("error");
          return;
        }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="bg-brand-bg min-h-[70vh] grid place-items-center py-20">
      <div className="container-x max-w-xl">
        <div className="rounded-[2rem] bg-white border border-brand-line shadow-card p-10 sm:p-12 text-center">
          {status === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 className="w-14 h-14 text-brand-red animate-spin mx-auto" />
              <h1 className="mt-6 font-display font-bold text-3xl text-brand-navy">Confirming your order…</h1>
              <p className="mt-3 text-brand-ink2">Just a moment while we confirm your payment.</p>
            </div>
          )}

          {status === "paid" && (
            <div data-testid="payment-paid">
              <span className="grid place-items-center w-20 h-20 rounded-full bg-emerald-100 mx-auto">
                <CheckCircle2 className="w-12 h-12 text-emerald-600" strokeWidth={2} />
              </span>
              <h1 className="mt-6 font-display font-black text-4xl text-brand-navy">You're protected!</h1>
              <p className="mt-3 text-lg text-brand-ink2">
                Thank you for your order. We'll ship your device free and a specialist will call to
                complete activation.
              </p>
              {order?.items?.length > 0 && (
                <div className="mt-7 text-left rounded-2xl bg-brand-bg border border-brand-line p-6">
                  <p className="font-bold text-brand-navy mb-3">Order summary</p>
                  <ul className="space-y-2">
                    {order.items.map((it, i) => (
                      <li key={i} className="flex justify-between text-brand-navy">
                        <span>{it.name} × {it.quantity}</span>
                      </li>
                    ))}
                  </ul>
                  {order.amount != null && (
                    <div className="mt-4 pt-4 border-t border-brand-line flex justify-between font-bold text-brand-navy">
                      <span>Total paid</span>
                      <span>{formatUSD(order.amount)}</span>
                    </div>
                  )}
                </div>
              )}
              <Link
                to="/"
                data-testid="success-home-link"
                className="btn-focus mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-brand-navy text-white font-bold text-lg hover:bg-brand-navy-700 transition-colors"
              >
                Back to home <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          )}

          {(status === "pending" || status === "error" || status === "failed") && (
            <div data-testid="payment-other">
              <span className="grid place-items-center w-20 h-20 rounded-full bg-brand-red/10 mx-auto">
                <AlertCircle className="w-12 h-12 text-brand-red" strokeWidth={2} />
              </span>
              <h1 className="mt-6 font-display font-bold text-3xl text-brand-navy">
                {status === "failed" ? "Payment not completed" : "Still processing"}
              </h1>
              <p className="mt-3 text-brand-ink2">
                {status === "failed"
                  ? "Your payment wasn't completed. You can try again from your cart."
                  : "Your payment is taking a little longer than usual. If you were charged, your order is safe — please call us to confirm."}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/cart" className="btn-focus inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-brand-red text-white font-bold hover:bg-brand-red-600 transition-colors">
                  Back to cart
                </Link>
                <a href={BRAND.phoneHref} className="btn-focus inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full border-2 border-brand-navy text-brand-navy font-bold hover:bg-brand-navy hover:text-white transition-colors">
                  <Phone className="w-5 h-5" /> {BRAND.phone}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
